import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import {
  AMERICAN_ROULETTE_WHEEL,
  getRouletteWheelPocketAngle,
  getRouletteWheelPocketColour,
} from "./rouletteWheelConstants";

const TWO_PI = Math.PI * 2;
const POCKET_COUNT = AMERICAN_ROULETTE_WHEEL.length;
const POCKET_ARC = TWO_PI / POCKET_COUNT;
const BALL_RADIUS = 0.114;
const BALL_TRACK_RADIUS = 2.08;
const OUTER_LABEL_INNER_RADIUS = 2.066;
const OUTER_LABEL_OUTER_RADIUS = 2.485;
const OUTER_LABEL_Y = 0.254;
const OUTER_LABEL_GAP_ANGLE = 0;
const WHEEL_SCENE_SCALE = 1.12;
const BASIN_SEATED_Y = -0.1;
const BASIN_SEATED_Z = -0.1;
const WHEEL_SEATED_Y = -0.205;
const WHEEL_SEATED_Z = -0.1;
const BALL_SEATING_Y_OFFSET = -0.077;
const OUTER_LABEL_INNER_Y = OUTER_LABEL_Y - 0.028;
const OUTER_LABEL_OUTER_Y = OUTER_LABEL_Y + 0.092;
const CENTRE_SPHERE_Y = 1.035;
const CENTRE_SPHERE_RADIUS = 0.17;
const CENTRE_SPINNER_INNER_RADIUS = CENTRE_SPHERE_RADIUS * 0.92;
const CENTRE_SPINNER_OUTER_RADIUS = 0.86;
const CENTRE_SPINNER_AXIS_ANGLE = getRouletteWheelPocketAngle("0");

const IDLE_WHEEL_SPEED = 0.12;
const PRE_LAUNCH_WHEEL_SPEED = 2.4;
const SPIN_WHEEL_START_SPEED = 5.8;
const SPIN_WHEEL_END_SPEED = 0.36;

const CAMERA_CLOCK_8PM_ANGLE = -Math.PI / 3;
const CAMERA_CLOCK_5PM_ANGLE = Math.PI / 6;
const CAMERA_CLOCK_7PM_ANGLE = -Math.PI / 6;

// Keep the enlarged ball slightly outward in the pocket so its inner silhouette
// clears the burgundy centre dome/feather at side camera angles.
const BALL_SETTLED_RADIUS = BALL_TRACK_RADIUS - 0.045;
const BALL_SETTLED_Y = 0.245 + BALL_SEATING_Y_OFFSET;
const BALL_WALL_ORBIT_RADIUS = 2.92;
const BALL_WALL_ORBIT_Y = 0.48;
const BALL_NUMBER_BAND_RADIUS = 2.22;
const BALL_NUMBER_BAND_Y = 0.315 + BALL_SEATING_Y_OFFSET;

const BALL_RAPID_ORBIT_MS = 2700;
const BALL_SLOW_ORBIT_MS = 1450;
const BALL_SPIRAL_MS = 1350;
const BALL_CLICK_SETTLE_MS = 1550;

const FINAL_APPROACH_FIRST_SLOWDOWN_POCKETS = 3;
const FINAL_APPROACH_SECOND_SLOWDOWN_POCKETS = 2;
const FINAL_APPROACH_TIP_SLOWDOWN_POCKETS = 1;
const FINAL_APPROACH_FIRST_SLOWDOWN_STRENGTH = 0.045;
const FINAL_APPROACH_SECOND_SLOWDOWN_STRENGTH = 0.072;
const FINAL_APPROACH_TIP_SLOWDOWN_STRENGTH = 0.105;

const BALL_NEAR_MISS_ROCKBACK_CHANCE = 1 / 3;
const BALL_NEAR_MISS_AFTER_POCKET_FRACTION = 0.42;
const BALL_NEAR_MISS_TIP_START_PROGRESS = 0.58;
const BALL_NEAR_MISS_TIP_PEAK_PROGRESS = 0.78;
const BALL_NEAR_MISS_RETURN_END_PROGRESS = 0.985;

const BALL_WINNING_POCKET_WOBBLE_START_PROGRESS = 0.72;
const BALL_WINNING_POCKET_WOBBLE_END_PROGRESS = 0.99;
const BALL_WINNING_POCKET_WOBBLE_ANGLE = POCKET_ARC * 0.16;
const BALL_WINNING_POCKET_WOBBLE_RADIUS = 0.014;
const BALL_WINNING_POCKET_WOBBLE_Y = 0.012;

function getOuterLabelBandYAtRadius(radius) {
  const bandWidth = OUTER_LABEL_OUTER_RADIUS - OUTER_LABEL_INNER_RADIUS;
  const progress = (radius - OUTER_LABEL_INNER_RADIUS) / bandWidth;

  return THREE.MathUtils.lerp(
    OUTER_LABEL_INNER_Y,
    OUTER_LABEL_OUTER_Y,
    progress
  );
}

function wheelLocalYToBasinLocalY(wheelLocalY) {
  return wheelLocalY + (WHEEL_SEATED_Y - BASIN_SEATED_Y) / WHEEL_SCENE_SCALE;
}

const BASIN_MATCHED_NUMBER_BAND_INNER_Y = wheelLocalYToBasinLocalY(OUTER_LABEL_INNER_Y);
const BASIN_MATCHED_NUMBER_BAND_OUTER_Y = wheelLocalYToBasinLocalY(OUTER_LABEL_OUTER_Y);

const COLOUR_MATERIALS = {
  red: "#ae1b2b",
  black: "#1f1f1f",
  green: "#186c44",
};

function polarToPosition(radius, angle, y = 0) {
  return [Math.sin(angle) * radius, y, Math.cos(angle) * radius];
}

function easeOutCubic(value) {
  return 1 - Math.pow(1 - value, 3);
}

function easeInOutSine(value) {
  return -(Math.cos(Math.PI * value) - 1) / 2;
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

function smoothstep(edge0, edge1, value) {
  const progress = clamp01((value - edge0) / (edge1 - edge0));

  return progress * progress * (3 - 2 * progress);
}

function getFinalApproachStartProgress(pocketDistance) {
  return 1 - (pocketDistance * POCKET_ARC) / (TWO_PI * 2.08);
}

function getFinalApproachFrictionLayer(progress, pocketDistance, strength) {
  const clampedProgress = clamp01(progress);
  const approachProgress = smoothstep(
    getFinalApproachStartProgress(pocketDistance),
    1,
    clampedProgress
  );
  const frictionBite = Math.sin(approachProgress * Math.PI);

  return frictionBite * strength * (1 - clampedProgress);
}

function applyFinalApproachFriction(progress) {
  const clampedProgress = clamp01(progress);
  const friction =
    getFinalApproachFrictionLayer(
      clampedProgress,
      FINAL_APPROACH_FIRST_SLOWDOWN_POCKETS,
      FINAL_APPROACH_FIRST_SLOWDOWN_STRENGTH
    ) +
    getFinalApproachFrictionLayer(
      clampedProgress,
      FINAL_APPROACH_SECOND_SLOWDOWN_POCKETS,
      FINAL_APPROACH_SECOND_SLOWDOWN_STRENGTH
    ) +
    getFinalApproachFrictionLayer(
      clampedProgress,
      FINAL_APPROACH_TIP_SLOWDOWN_POCKETS,
      FINAL_APPROACH_TIP_SLOWDOWN_STRENGTH
    );

  return clamp01(clampedProgress - friction);
}

function getNearMissRockback(progress, enabled) {
  if (!enabled) {
    return {
      angleOffset: 0,
      radiusOffset: 0,
      yOffset: 0,
    };
  }

  const clampedProgress = clamp01(progress);
  const tipProgress = smoothstep(
    BALL_NEAR_MISS_TIP_START_PROGRESS,
    BALL_NEAR_MISS_TIP_PEAK_PROGRESS,
    clampedProgress
  );
  const returnProgress = smoothstep(
    BALL_NEAR_MISS_TIP_PEAK_PROGRESS,
    BALL_NEAR_MISS_RETURN_END_PROGRESS,
    clampedProgress
  );
  const rockAmount = tipProgress * (1 - returnProgress);

  return {
    // Negative angle follows the ball's clockwise travel into the pocket after
    // the winner. The fraction stays under half a pocket by design.
    angleOffset: -POCKET_ARC * BALL_NEAR_MISS_AFTER_POCKET_FRACTION * rockAmount,
    radiusOffset: 0.018 * rockAmount,
    yOffset: 0.018 * rockAmount,
  };
}

function getWinningPocketWobble(progress, enabled) {
  if (!enabled) {
    return {
      angleOffset: 0,
      radiusOffset: 0,
      yOffset: 0,
    };
  }

  const clampedProgress = clamp01(progress);
  const wobbleProgress = smoothstep(
    BALL_WINNING_POCKET_WOBBLE_START_PROGRESS,
    BALL_WINNING_POCKET_WOBBLE_END_PROGRESS,
    clampedProgress
  );
  const settleFade = 1 - smoothstep(0.9, 1, clampedProgress);
  const wobbleAmount =
    Math.sin(wobbleProgress * Math.PI * 2.5) * settleFade * (1 - clampedProgress * 0.18);

  return {
    angleOffset: BALL_WINNING_POCKET_WOBBLE_ANGLE * wobbleAmount,
    radiusOffset: BALL_WINNING_POCKET_WOBBLE_RADIUS * Math.abs(wobbleAmount),
    yOffset: BALL_WINNING_POCKET_WOBBLE_Y * Math.abs(wobbleAmount),
  };
}

function getNextClockPassAngle(currentUnwrappedAngle, targetClockAngle) {
  let targetAngle =
    targetClockAngle +
    Math.ceil((currentUnwrappedAngle - targetClockAngle) / TWO_PI) * TWO_PI;

  if (targetAngle <= currentUnwrappedAngle) {
    targetAngle += TWO_PI;
  }

  return targetAngle;
}

function getWheelSpeedAtSpinProgress(progress) {
  return THREE.MathUtils.lerp(
    SPIN_WHEEL_START_SPEED,
    SPIN_WHEEL_END_SPEED,
    easeOutCubic(clamp01(progress))
  );
}

function getWheelRotationIntegralAtSpinProgress(progress) {
  const clampedProgress = clamp01(progress);

  return (
    SPIN_WHEEL_START_SPEED * clampedProgress +
    (SPIN_WHEEL_END_SPEED - SPIN_WHEEL_START_SPEED) *
      (
        1.5 * clampedProgress * clampedProgress -
        clampedProgress * clampedProgress * clampedProgress +
        0.25 *
          clampedProgress *
          clampedProgress *
          clampedProgress *
          clampedProgress
      )
  );
}

function estimateWheelRotationDeltaForSpinProgress(startProgress, endProgress, durationMs) {
  return (
    (durationMs / 1000) *
    (
      getWheelRotationIntegralAtSpinProgress(endProgress) -
      getWheelRotationIntegralAtSpinProgress(startProgress)
    )
  );
}

function getClockwiseTargetAngleAfterMinimumTravel(
  currentUnwrappedAngle,
  movingTargetAngle,
  minimumClockwiseTravel
) {
  const latestAllowedTargetAngle = currentUnwrappedAngle - minimumClockwiseTravel;
  let targetAngle =
    movingTargetAngle +
    Math.floor((latestAllowedTargetAngle - movingTargetAngle) / TWO_PI) * TWO_PI;

  if (targetAngle > latestAllowedTargetAngle) {
    targetAngle -= TWO_PI;
  }

  return targetAngle;
}

function setBallPolarPosition(ball, radius, angle, y) {
  const [x, positionY, z] = polarToPosition(radius, angle, y);
  ball.position.set(x, positionY, z);
}

function emitSpinSfxOnce(spinState, eventName, onRouletteSfx) {
  if (!spinState || !eventName || !onRouletteSfx) {
    return;
  }

  if (!spinState.sfxEvents) {
    spinState.sfxEvents = {};
  }

  if (spinState.sfxEvents[eventName]) {
    return;
  }

  spinState.sfxEvents[eventName] = true;
  onRouletteSfx(eventName);
}

class RouletteWheelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn("Roulette 3D wheel failed to render:", error);
  }

  render() {
    if (this.state.failed) {
      return <RouletteWheelFallback status={this.props.status} />;
    }

    return this.props.children;
  }
}

function RouletteWheelFallback({ status }) {
  return (
    <div className="roulette-wheel-fallback" role="img" aria-label="Roulette wheel fallback">
      <span>ROULETTE WHEEL</span>
      <strong>{status || "DRAFT"}</strong>
      <p>3D renderer unavailable on this device.</p>
    </div>
  );
}

function CameraRig() {
  const { camera, size } = useThree();

  useEffect(() => {
    camera.position.set(0, 3.88, 5.5);
    camera.lookAt(0, -0.3, 0.25);
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height]);

  return null;
}

function createRadialProfileGeometry(profile, segments = 224) {
  const positions = [];
  const uvs = [];
  const indices = [];
  const innerRadius = profile[0].radius;
  const outerRadius = profile[profile.length - 1].radius;
  const radiusRange = Math.max(outerRadius - innerRadius, 0.001);

  for (let index = 0; index <= segments; index += 1) {
    const angle = (index / segments) * TWO_PI;
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);

    profile.forEach((point) => {
      positions.push(sin * point.radius, point.y, cos * point.radius);
      uvs.push(index / segments, (point.radius - innerRadius) / radiusRange);
    });
  }

  const ringCount = profile.length;

  for (let index = 0; index < segments; index += 1) {
    const current = index * ringCount;
    const next = (index + 1) * ringCount;

    for (let ring = 0; ring < ringCount - 1; ring += 1) {
      const innerCurrent = current + ring;
      const outerCurrent = current + ring + 1;
      const innerNext = next + ring;
      const outerNext = next + ring + 1;

      indices.push(innerCurrent, outerCurrent, outerNext);
      indices.push(innerCurrent, outerNext, innerNext);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
}

function createSlopedAnnularGeometry(innerRadius, outerRadius, innerY, outerY, segments = 224) {
  return createRadialProfileGeometry(
    [
      { radius: innerRadius, y: innerY },
      { radius: outerRadius, y: outerY },
    ],
    segments
  );
}

function WoodenBasin() {
  const basinContinuousCraterGeometry = useMemo(
    () =>
      createRadialProfileGeometry(
        [
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.022,
            y: BASIN_MATCHED_NUMBER_BAND_OUTER_Y - 0.001,
          },
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.062,
            y: BASIN_MATCHED_NUMBER_BAND_OUTER_Y - 0.018,
          },
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.125,
            y: THREE.MathUtils.lerp(
              BASIN_MATCHED_NUMBER_BAND_OUTER_Y,
              BASIN_MATCHED_NUMBER_BAND_INNER_Y,
              0.42
            ),
          },
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.235,
            y: THREE.MathUtils.lerp(
              BASIN_MATCHED_NUMBER_BAND_OUTER_Y,
              BASIN_MATCHED_NUMBER_BAND_INNER_Y,
              0.78
            ),
          },
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.385,
            y: BASIN_MATCHED_NUMBER_BAND_INNER_Y - 0.014,
          },
          { radius: 3.02, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.04 },
          { radius: 3.18, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.158 },
          { radius: 3.31, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.272 },
          { radius: 3.39, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.365 },
        ],
        256
      ),
    []
  );
  const basinVarnishGeometry = useMemo(
    () =>
      createRadialProfileGeometry(
        [
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.034,
            y: BASIN_MATCHED_NUMBER_BAND_OUTER_Y + 0.002,
          },
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.14,
            y: THREE.MathUtils.lerp(
              BASIN_MATCHED_NUMBER_BAND_OUTER_Y,
              BASIN_MATCHED_NUMBER_BAND_INNER_Y,
              0.52
            ),
          },
          { radius: 2.84, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y - 0.004 },
          { radius: 3.06, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.076 },
          { radius: 3.24, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.208 },
          { radius: 3.36, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.333 },
        ],
        256
      ),
    []
  );
  const basinInnerDepthGeometry = useMemo(
    () =>
      createRadialProfileGeometry(
        [
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.036,
            y: BASIN_MATCHED_NUMBER_BAND_OUTER_Y - 0.006,
          },
          {
            radius: OUTER_LABEL_OUTER_RADIUS + 0.16,
            y: THREE.MathUtils.lerp(
              BASIN_MATCHED_NUMBER_BAND_OUTER_Y,
              BASIN_MATCHED_NUMBER_BAND_INNER_Y,
              0.64
            ),
          },
          { radius: 2.84, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y - 0.026 },
          { radius: 3.08, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.04 },
          { radius: 3.28, y: BASIN_MATCHED_NUMBER_BAND_INNER_Y + 0.215 },
        ],
        256
      ),
    []
  );

  return (
    <group>
      <mesh geometry={basinContinuousCraterGeometry} receiveShadow={false}>
        <meshStandardMaterial
          color="#37101f"
          roughness={0.54}
          metalness={0.22}
          emissive="#1a0510"
          emissiveIntensity={0.2}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-1.65}
          polygonOffsetUnits={-1.65}
        />
      </mesh>

      <mesh geometry={basinVarnishGeometry} receiveShadow={false}>
        <meshStandardMaterial
          color="#6d142e"
          roughness={0.55}
          metalness={0.18}
          emissive="#260711"
          emissiveIntensity={0.115}
          transparent
          opacity={0.125}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-1.85}
          polygonOffsetUnits={-1.85}
        />
      </mesh>

      <mesh geometry={basinInnerDepthGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#000000"
          transparent
          opacity={0.045}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2.2}
          polygonOffsetUnits={-2.2}
        />
      </mesh>

      <mesh castShadow receiveShadow={false} position={[0, 0.333, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[3.285, 0.165, 28, 224]} />
        <meshStandardMaterial
          color="#210713"
          roughness={0.4}
          metalness={0.24}
          emissive="#0d0307"
          emissiveIntensity={0.17}
        />
      </mesh>

      <mesh castShadow receiveShadow={false} position={[0, 0.432, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[3.285, 0.024, 12, 224]} />
        <meshStandardMaterial
          color="#7a1835"
          roughness={0.34}
          metalness={0.38}
          emissive="#300916"
          emissiveIntensity={0.2}
        />
      </mesh>

      <mesh castShadow receiveShadow={false} position={[0, 0.246, 0]}>
        <cylinderGeometry args={[3.34, 3.45, 0.5, 224, 1, true]} />
        <meshStandardMaterial
          color="#19050d"
          roughness={0.46}
          metalness={0.18}
          emissive="#090205"
          emissiveIntensity={0.13}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh receiveShadow={false} position={[0, 0.335, 0]}>
        <cylinderGeometry args={[3.19, 3.255, 0.4, 224, 1, true]} />
        <meshBasicMaterial
          color="#070101"
          transparent
          opacity={0.11}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh castShadow receiveShadow={false} position={[0, BASIN_MATCHED_NUMBER_BAND_OUTER_Y - 0.012, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[2.518, 0.016, 14, 224]} />
        <meshStandardMaterial
          color="#efc96f"
          roughness={0.18}
          metalness={0.94}
          emissive="#6f450f"
          emissiveIntensity={0.16}
        />
      </mesh>

      <mesh castShadow receiveShadow={false} position={[0, BASIN_MATCHED_NUMBER_BAND_OUTER_Y + 0.004, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[2.518, 0.0055, 8, 224]} />
        <meshStandardMaterial
          color="#fff0ad"
          roughness={0.16}
          metalness={0.88}
          emissive="#6f450f"
          emissiveIntensity={0.12}
        />
      </mesh>

      <mesh receiveShadow={false} position={[0, BASIN_MATCHED_NUMBER_BAND_INNER_Y - 0.21, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[2.54, 3.54, 224]} />
        <meshStandardMaterial
          color="#070101"
          roughness={0.6}
          metalness={0.02}
          emissive="#010000"
          emissiveIntensity={0.02}
          transparent
          opacity={0.12}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh castShadow receiveShadow={false} position={[0, BASIN_MATCHED_NUMBER_BAND_INNER_Y - 0.182, 0]}>
        <cylinderGeometry args={[3.29, 3.52, 0.28, 224, 1, true]} />
        <meshStandardMaterial
          color="#1d0610"
          roughness={0.5}
          metalness={0.16}
          emissive="#0a0205"
          emissiveIntensity={0.15}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

function canvasPointFromWheelAngle(center, scale, radius, angle) {
  return [
    center + Math.sin(angle) * radius * scale,
    center - Math.cos(angle) * radius * scale,
  ];
}

function drawPrintedLabelSector(ctx, center, scale, innerRadius, outerRadius, startAngle, endAngle, colour) {
  const [innerStartX, innerStartY] = canvasPointFromWheelAngle(center, scale, innerRadius, startAngle);
  const [outerStartX, outerStartY] = canvasPointFromWheelAngle(center, scale, outerRadius, startAngle);

  ctx.beginPath();
  ctx.moveTo(innerStartX, innerStartY);
  ctx.lineTo(outerStartX, outerStartY);
  ctx.arc(center, center, outerRadius * scale, startAngle - Math.PI / 2, endAngle - Math.PI / 2, false);
  ctx.lineTo(
    center + Math.sin(endAngle) * innerRadius * scale,
    center - Math.cos(endAngle) * innerRadius * scale
  );
  ctx.arc(center, center, innerRadius * scale, endAngle - Math.PI / 2, startAngle - Math.PI / 2, true);
  ctx.closePath();

  ctx.fillStyle = colour;
  ctx.fill();
}

function drawPrintedGoldArc(ctx, center, scale, radius, lineWidth, alpha = 0.9) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(center, center, radius * scale, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(239, 201, 111, ${alpha})`;
  ctx.lineWidth = lineWidth;
  ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
  ctx.shadowBlur = 2;
  ctx.stroke();
  ctx.restore();
}

function drawPrintedGoldDivider(ctx, center, scale, innerRadius, outerRadius, angle) {
  const [innerX, innerY] = canvasPointFromWheelAngle(center, scale, innerRadius, angle);
  const [outerX, outerY] = canvasPointFromWheelAngle(center, scale, outerRadius, angle);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(innerX, innerY);
  ctx.lineTo(outerX, outerY);
  ctx.strokeStyle = "rgba(239, 201, 111, 0.92)";
  ctx.lineWidth = 4;
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = 2;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(innerX, innerY);
  ctx.lineTo(outerX, outerY);
  ctx.strokeStyle = "rgba(255, 240, 173, 0.38)";
  ctx.lineWidth = 1.25;
  ctx.shadowBlur = 0;
  ctx.stroke();
  ctx.restore();
}

function drawPrintedWheelNumber(ctx, center, scale, radius, angle, pocket) {
  const [x, y] = canvasPointFromWheelAngle(center, scale, radius, angle);
  const isDoubleZero = pocket === "00";

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle + Math.PI);
  ctx.scale(-1, 1);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${isDoubleZero ? 44 : 48}px Arial Black, Arial, Helvetica, sans-serif`;
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.lineWidth = 7;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.82)";
  ctx.fillStyle = "#f7f7f7";
  ctx.shadowColor = "rgba(0, 0, 0, 0.72)";
  ctx.shadowBlur = 3;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 2;
  ctx.strokeText(pocket, 0, 0);
  ctx.fillText(pocket, 0, 0);
  ctx.restore();
}

function createPrintedLabelBandGeometry(innerRadius, outerRadius, innerY, outerY = innerY, segments = 256) {
  const positions = [];
  const uvs = [];
  const indices = [];

  for (let index = 0; index <= segments; index += 1) {
    const angle = (index / segments) * TWO_PI;
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);

    positions.push(sin * innerRadius, innerY, cos * innerRadius);
    positions.push(sin * outerRadius, outerY, cos * outerRadius);

    uvs.push(0.5 + (sin * innerRadius) / (outerRadius * 2));
    uvs.push(0.5 - (cos * innerRadius) / (outerRadius * 2));
    uvs.push(0.5 + (sin * outerRadius) / (outerRadius * 2));
    uvs.push(0.5 - (cos * outerRadius) / (outerRadius * 2));

    if (index < segments) {
      const innerCurrent = index * 2;
      const outerCurrent = innerCurrent + 1;
      const innerNext = innerCurrent + 2;
      const outerNext = innerCurrent + 3;

      indices.push(innerCurrent, outerCurrent, outerNext);
      indices.push(innerCurrent, outerNext, innerNext);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
}

function createPrintedLabelBandTexture() {
  if (typeof document === "undefined") {
    return null;
  }

  const size = 2048;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  const center = size / 2;
  const scale = (size * 0.5 * 0.985) / OUTER_LABEL_OUTER_RADIUS;

  ctx.clearRect(0, 0, size, size);

  AMERICAN_ROULETTE_WHEEL.forEach((pocket, index) => {
    const angle = (index / POCKET_COUNT) * TWO_PI;
    const startAngle = angle - POCKET_ARC / 2 + OUTER_LABEL_GAP_ANGLE;
    const endAngle = angle + POCKET_ARC / 2 - OUTER_LABEL_GAP_ANGLE;
    const colour = getRouletteWheelPocketColour(pocket);
    const materialColour = COLOUR_MATERIALS[colour];

    drawPrintedLabelSector(
      ctx,
      center,
      scale,
      OUTER_LABEL_INNER_RADIUS,
      OUTER_LABEL_OUTER_RADIUS,
      startAngle,
      endAngle,
      materialColour
    );

    ctx.save();
    ctx.globalCompositeOperation = "source-atop";
    ctx.globalAlpha = colour === "black" ? 0.18 : 0.12;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
    ctx.lineWidth = 2;

    for (let offset = -340; offset < 340; offset += 28) {
      ctx.beginPath();
      ctx.moveTo(center - 360, center + offset);
      ctx.lineTo(center + 360, center + offset + 360);
      ctx.stroke();
    }

    ctx.restore();
  });

  drawPrintedGoldArc(ctx, center, scale, OUTER_LABEL_INNER_RADIUS, 4.25, 0.74);
  drawPrintedGoldArc(ctx, center, scale, OUTER_LABEL_OUTER_RADIUS, 2.7, 0.48);

  AMERICAN_ROULETTE_WHEEL.forEach((_, index) => {
    const dividerAngle = (index / POCKET_COUNT) * TWO_PI + POCKET_ARC / 2;
    drawPrintedGoldDivider(
      ctx,
      center,
      scale,
      OUTER_LABEL_INNER_RADIUS,
      OUTER_LABEL_OUTER_RADIUS,
      dividerAngle
    );
  });

  const labelRadius = OUTER_LABEL_INNER_RADIUS + (OUTER_LABEL_OUTER_RADIUS - OUTER_LABEL_INNER_RADIUS) * 0.585;

  AMERICAN_ROULETTE_WHEEL.forEach((pocket, index) => {
    const angle = (index / POCKET_COUNT) * TWO_PI;

    drawPrintedWheelNumber(
      ctx,
      center,
      scale,
      labelRadius,
      angle,
      pocket
    );
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  texture.flipY = false;
  texture.needsUpdate = true;

  return texture;
}

function RoulettePrintedLabelBand() {
  const texture = useMemo(() => createPrintedLabelBandTexture(), []);
  const geometry = useMemo(
    () =>
      createPrintedLabelBandGeometry(
        OUTER_LABEL_INNER_RADIUS,
        OUTER_LABEL_OUTER_RADIUS,
        OUTER_LABEL_INNER_Y,
        OUTER_LABEL_OUTER_Y,
        512
      ),
    []
  );

  if (!texture || !geometry) {
    return null;
  }

  return (
    <mesh geometry={geometry} receiveShadow>
      <meshStandardMaterial
        map={texture}
        emissive="#ffffff"
        emissiveMap={texture}
        emissiveIntensity={0.2}
        transparent
        roughness={0.42}
        metalness={0.05}
        side={THREE.DoubleSide}
        polygonOffset
        polygonOffsetFactor={-1.75}
        polygonOffsetUnits={-1.75}
      />
    </mesh>
  );
}

function SlopedOuterGoldTrim() {
  const trimGeometry = useMemo(() => {
    const innerRadius = OUTER_LABEL_OUTER_RADIUS - 0.006;
    const outerRadius = OUTER_LABEL_OUTER_RADIUS + 0.038;

    return createSlopedAnnularGeometry(
      innerRadius,
      outerRadius,
      getOuterLabelBandYAtRadius(innerRadius) + 0.006,
      getOuterLabelBandYAtRadius(outerRadius) + 0.006,
      512
    );
  }, []);

  const highlightGeometry = useMemo(() => {
    const innerRadius = OUTER_LABEL_OUTER_RADIUS + 0.012;
    const outerRadius = OUTER_LABEL_OUTER_RADIUS + 0.03;

    return createSlopedAnnularGeometry(
      innerRadius,
      outerRadius,
      getOuterLabelBandYAtRadius(innerRadius) + 0.009,
      getOuterLabelBandYAtRadius(outerRadius) + 0.009,
      512
    );
  }, []);

  return (
    <>
      <mesh geometry={trimGeometry} receiveShadow={false}>
        <meshStandardMaterial
          color="#efc96f"
          roughness={0.18}
          metalness={0.94}
          emissive="#6f450f"
          emissiveIntensity={0.16}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2.55}
          polygonOffsetUnits={-2.55}
        />
      </mesh>

      <mesh geometry={highlightGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#fff0ad"
          transparent
          opacity={0.13}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2.9}
          polygonOffsetUnits={-2.9}
        />
      </mesh>
    </>
  );
}

function WheelCraterSurface() {
  const geometry = useMemo(
    () =>
      createRadialProfileGeometry(
        [
          { radius: 0.46, y: 0.138 },
          { radius: 1.22, y: 0.152 },
          { radius: 1.72, y: 0.172 },
          { radius: 2.06, y: 0.218 },
          { radius: 2.28, y: 0.278 },
          { radius: 2.5, y: 0.338 },
        ],
        256
      ),
    []
  );

  return (
    <mesh geometry={geometry} receiveShadow>
      <meshStandardMaterial
        color="#080706"
        roughness={0.62}
        metalness={0.16}
        emissive="#020101"
        emissiveIntensity={0.04}
        side={THREE.DoubleSide}
        polygonOffset
        polygonOffsetFactor={1.25}
        polygonOffsetUnits={1.25}
      />
    </mesh>
  );
}

function createBurgundyDomeGeometry(
  domeRadius = 1.74,
  baseY = 0.19,
  domeHeight = 0.5,
  radialSegments = 34,
  angularSegments = 256
) {
  const positions = [];
  const uvs = [];
  const indices = [];

  for (let radialIndex = 0; radialIndex <= radialSegments; radialIndex += 1) {
    const radialProgress = radialIndex / radialSegments;
    const radius = domeRadius * radialProgress;
    const normalizedRadius = radius / domeRadius;

    const domeCurve = Math.sqrt(Math.max(0, 1 - normalizedRadius * normalizedRadius));
    const y = baseY + domeHeight * domeCurve;

    for (let angularIndex = 0; angularIndex <= angularSegments; angularIndex += 1) {
      const angle = (angularIndex / angularSegments) * TWO_PI;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);

      positions.push(sin * radius, y, cos * radius);
      uvs.push(angularIndex / angularSegments, radialProgress);
    }
  }

  const rowLength = angularSegments + 1;

  for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
    for (let angularIndex = 0; angularIndex < angularSegments; angularIndex += 1) {
      const current = radialIndex * rowLength + angularIndex;
      const next = current + rowLength;

      indices.push(current, next, current + 1);
      indices.push(current + 1, next, next + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
}

function InnerBurgundyHill() {
  const domeGeometry = useMemo(
    () => createBurgundyDomeGeometry(1.74, 0.19, 0.5, 34, 256),
    []
  );

  const centreSheenGeometry = useMemo(
    () => createBurgundyDomeGeometry(0.42, 0.658, 0.034, 14, 144),
    []
  );

  const centreSheenFeatherGeometry = useMemo(
    () =>
      createSlopedAnnularGeometry(0.34, 0.48, 0.681, 0.654, 144),
    []
  );

  const outerFeatherGeometry = useMemo(
    () =>
      createSlopedAnnularGeometry(1.64, 1.8, 0.196, 0.184, 256),
    []
  );

  return (
    <>
      <mesh geometry={domeGeometry} receiveShadow>
        <meshStandardMaterial
          color="#401123"
          roughness={0.52}
          metalness={0.2}
          emissive="#230713"
          emissiveIntensity={0.28}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-1.45}
          polygonOffsetUnits={-1.45}
        />
      </mesh>

      <mesh geometry={centreSheenGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#8a1d3b"
          transparent
          opacity={0.045}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-1.8}
          polygonOffsetUnits={-1.8}
        />
      </mesh>

      <mesh geometry={centreSheenFeatherGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#401123"
          transparent
          opacity={0.025}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-1.95}
          polygonOffsetUnits={-1.95}
        />
      </mesh>

      <mesh geometry={outerFeatherGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#230713"
          transparent
          opacity={0.17}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2.05}
          polygonOffsetUnits={-2.05}
        />
      </mesh>
    </>
  );
}

function GoldPocketFloorBand() {
  const floorGeometry = useMemo(
    () =>
      createRadialProfileGeometry(
        [
          { radius: 1.66, y: 0.214 },
          { radius: 1.84, y: 0.22 },
          { radius: 2.045, y: 0.226 },
        ],
        256
      ),
    []
  );

  const innerHighlightGeometry = useMemo(
    () => createSlopedAnnularGeometry(1.66, 1.705, 0.222, 0.214, 256),
    []
  );

  const outerHighlightGeometry = useMemo(
    () => createSlopedAnnularGeometry(2.0, 2.052, 0.222, 0.23, 256),
    []
  );

  return (
    <>
      <mesh geometry={floorGeometry} receiveShadow>
        <meshStandardMaterial
          color="#efc96f"
          roughness={0.24}
          metalness={0.9}
          emissive="#6f450f"
          emissiveIntensity={0.14}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-1.65}
          polygonOffsetUnits={-1.65}
        />
      </mesh>

      <mesh geometry={innerHighlightGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#fff0ad"
          transparent
          opacity={0.15}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2.05}
          polygonOffsetUnits={-2.05}
        />
      </mesh>

      <mesh geometry={outerHighlightGeometry} receiveShadow={false}>
        <meshBasicMaterial
          color="#fff0ad"
          transparent
          opacity={0.09}
          depthWrite={false}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2.08}
          polygonOffsetUnits={-2.08}
        />
      </mesh>
    </>
  );
}

function PocketPlate({ index }) {
  const angle = (index / POCKET_COUNT) * TWO_PI;
  const dividerAngle = angle + POCKET_ARC / 2;

  return (
    <>
      <group position={polarToPosition(1.82, angle, 0.19)} rotation={[0, angle, 0]}>
        <mesh receiveShadow={false} position={[-0.101, 0.024, 0]}>
          <boxGeometry args={[0.012, 0.092, 0.382]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.22}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0.101, 0.024, 0]}>
          <boxGeometry args={[0.012, 0.092, 0.382]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.22}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.026, -0.185]}>
          <boxGeometry args={[0.202, 0.086, 0.014]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.22}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.027, -0.174]}>
          <boxGeometry args={[0.156, 0.036, 0.005]} />
          <meshBasicMaterial
            color="#6f450f"
            transparent
            opacity={0.055}
            depthWrite={false}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.051, -0.171]}>
          <boxGeometry args={[0.144, 0.006, 0.004]} />
          <meshBasicMaterial
            color="#fff0ad"
            transparent
            opacity={0.075}
            depthWrite={false}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.026, 0.185]}>
          <boxGeometry args={[0.202, 0.086, 0.014]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.28}
            metalness={0.86}
            emissive="#6f450f"
            emissiveIntensity={0.11}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.054, 0.177]}>
          <boxGeometry args={[0.166, 0.006, 0.005]} />
          <meshBasicMaterial
            color="#6f450f"
            transparent
            opacity={0.045}
            depthWrite={false}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.077, -0.177]}>
          <boxGeometry args={[0.166, 0.005, 0.01]} />
          <meshBasicMaterial
            color="#fff0ad"
            transparent
            opacity={0.18}
            depthWrite={false}
          />
        </mesh>
      </group>

      <group position={polarToPosition(1.785, dividerAngle, 0.22)} rotation={[0, dividerAngle, 0]}>
        <mesh receiveShadow={false}>
          <boxGeometry args={[0.012, 0.084, 0.55]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.22}
          />
        </mesh>
      </group>

    </>
  );
}


function createSpinnerHandleGeometry(profile, radialSegments = 40) {
  const positions = [];
  const uvs = [];
  const indices = [];

  profile.forEach((point, profileIndex) => {
    for (let radialIndex = 0; radialIndex <= radialSegments; radialIndex += 1) {
      const angle = (radialIndex / radialSegments) * TWO_PI;
      const x = Math.cos(angle) * point.radius;
      const y = Math.sin(angle) * point.radius;

      positions.push(x, y, point.z);
      uvs.push(radialIndex / radialSegments, profileIndex / (profile.length - 1));
    }
  });

  const rowLength = radialSegments + 1;

  for (let profileIndex = 0; profileIndex < profile.length - 1; profileIndex += 1) {
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      const current = profileIndex * rowLength + radialIndex;
      const next = current + rowLength;

      indices.push(current, current + 1, next + 1);
      indices.push(current, next + 1, next);
    }
  }

  const startCenterIndex = positions.length / 3;
  positions.push(0, 0, profile[0].z);
  uvs.push(0.5, 0);

  for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
    indices.push(startCenterIndex, radialIndex + 1, radialIndex);
  }

  const endCenterIndex = positions.length / 3;
  const endRingStart = (profile.length - 1) * rowLength;
  positions.push(0, 0, profile[profile.length - 1].z);
  uvs.push(0.5, 1);

  for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
    indices.push(endCenterIndex, endRingStart + radialIndex, endRingStart + radialIndex + 1);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
}

function SmoothGoldSpinnerHandle() {
  const geometry = useMemo(
    () =>
      createSpinnerHandleGeometry(
        [
          { z: CENTRE_SPINNER_INNER_RADIUS * 0.96, radius: 0.032 },
          { z: 0.205, radius: 0.046 },
          { z: 0.28, radius: 0.061 },
          { z: 0.39, radius: 0.076 },
          { z: 0.52, radius: 0.07 },
          { z: 0.63, radius: 0.057 },
          { z: 0.72, radius: 0.074 },
          { z: 0.815, radius: 0.092 },
          { z: CENTRE_SPINNER_OUTER_RADIUS, radius: 0.068 },
          { z: CENTRE_SPINNER_OUTER_RADIUS + 0.035, radius: 0.024 },
        ],
        40
      ),
    []
  );

  const highlightGeometry = useMemo(
    () =>
      createSpinnerHandleGeometry(
        [
          { z: 0.25, radius: 0.018 },
          { z: 0.4, radius: 0.024 },
          { z: 0.57, radius: 0.022 },
          { z: 0.76, radius: 0.027 },
          { z: 0.84, radius: 0.02 },
        ],
        24
      ),
    []
  );

  return (
    <group position={[0, CENTRE_SPHERE_Y, 0]}>
      <mesh geometry={geometry} castShadow receiveShadow>
        <meshStandardMaterial
          color="#efc96f"
          roughness={0.18}
          metalness={0.94}
          emissive="#6f450f"
          emissiveIntensity={0.16}
        />
      </mesh>

      <mesh
        geometry={highlightGeometry}
        receiveShadow={false}
        position={[0.018, 0.026, 0]}
      >
        <meshBasicMaterial
          color="#fff0ad"
          transparent
          opacity={0.12}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

function GoldCentreSpinnerCross() {
  const rodAngles = useMemo(
    () => [
      CENTRE_SPINNER_AXIS_ANGLE,
      CENTRE_SPINNER_AXIS_ANGLE + Math.PI,
      CENTRE_SPINNER_AXIS_ANGLE + Math.PI / 2,
      CENTRE_SPINNER_AXIS_ANGLE + (Math.PI * 3) / 2,
    ],
    []
  );

  return (
    <group>
      {rodAngles.map((angle, index) => (
        <group
          key={`centre-spinner-rod-${index}`}
          rotation={[0, angle, 0]}
        >
          <SmoothGoldSpinnerHandle />
        </group>
      ))}
    </group>
  );
}

function RouletteStatusTextureLabel({ text }) {
  const texture = useMemo(() => {
    const canvas = document.createElement("canvas");
    const width = 512;
    const height = 96;
    const context = canvas.getContext("2d");

    canvas.width = width;
    canvas.height = height;

    context.clearRect(0, 0, width, height);
    context.font = "800 34px Arial, sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.lineJoin = "round";
    context.lineWidth = 6;
    context.strokeStyle = "#000000";
    context.fillStyle = "#db3333";
    context.strokeText(text, width / 2, height / 2);
    context.fillText(text, width / 2, height / 2);

    const nextTexture = new THREE.CanvasTexture(canvas);
    nextTexture.colorSpace = THREE.SRGBColorSpace;
    nextTexture.anisotropy = 2;
    nextTexture.needsUpdate = true;

    return nextTexture;
  }, [text]);

  useEffect(() => {
    return () => {
      texture.dispose();
    };
  }, [texture]);

  return (
    <mesh position={[0, -0.72, 3.18]}>
      <planeGeometry args={[1.55, 0.29]} />
      <meshBasicMaterial map={texture} transparent />
    </mesh>
  );
}

function RouletteWheelScene({
  status,
  result,
  spinNonce,
  onSpinComplete,
  onRouletteSfx,
}) {
  const wheelRef = useRef(null);
  const ballRef = useRef(null);
  const spinStateRef = useRef(null);
  const lastSpinNonceRef = useRef(spinNonce);
  const settledAngleRef = useRef(getRouletteWheelPocketAngle(result || "0"));
  const ballAngleRef = useRef(settledAngleRef.current);
  const [visualResult, setVisualResult] = useState(result || null);
  const onSpinCompleteRef = useRef(onSpinComplete);

  useEffect(() => {
    onSpinCompleteRef.current = onSpinComplete;
  }, [onSpinComplete]);

  useEffect(() => {
    if (!result) {
      return;
    }

    if (String(status || "") === "SETTLED") {
      const settledAngle = getRouletteWheelPocketAngle(result);
      settledAngleRef.current = settledAngle;
      ballAngleRef.current = settledAngle;
      setVisualResult(result);
    }
  }, [result, status]);

  useEffect(() => {
    if (!result || spinNonce === lastSpinNonceRef.current) {
      return;
    }

    lastSpinNonceRef.current = spinNonce;

    const targetAngle = getRouletteWheelPocketAngle(result);
    const sourcePocketAngle = settledAngleRef.current;
    const currentWheelRotation = wheelRef.current?.rotation?.y || 0;
    const sourcePocketWorldAngle = sourcePocketAngle + currentWheelRotation;
    const pickupPocketAngle = getNextClockPassAngle(
      sourcePocketWorldAngle,
      CAMERA_CLOCK_8PM_ANGLE
    );
    const launchPocketAngle = getNextClockPassAngle(
      pickupPocketAngle + 0.0001,
      CAMERA_CLOCK_7PM_ANGLE
    );

    spinStateRef.current = {
      phase: "WAIT_PICKUP",
      startedAt: performance.now(),
      sourcePocketAngle,
      pickupPocketAngle,
      launchPocketAngle,
      launchedAt: null,
      decelStartAngle: null,
      decelEndAngle: null,
      finalApproachSlowdownStarted: false,
      finalApproachSecondSlowdownStarted: false,
      finalApproachTipSlowdownStarted: false,
      nearMissRockbackEnabled: Math.random() < BALL_NEAR_MISS_ROCKBACK_CHANCE,
      winningPocketWobbleEnabled: false,
      sfxEvents: {},
      clickStartAngle: null,
      finalAngle: targetAngle,
      result: String(result),
      completed: false,
    };

    spinStateRef.current.winningPocketWobbleEnabled =
      !spinStateRef.current.nearMissRockbackEnabled;

    setVisualResult(null);
  }, [result, spinNonce]);

  useFrame((state, delta) => {
    const wheel = wheelRef.current;
    const ball = ballRef.current;

    if (!wheel || !ball) {
      return;
    }

    const spinState = spinStateRef.current;

    if (!spinState) {
      wheel.rotation.y += delta * IDLE_WHEEL_SPEED;
      ball.visible = true;

      const anchoredAngle = settledAngleRef.current + wheel.rotation.y;
      setBallPolarPosition(ball, BALL_SETTLED_RADIUS, anchoredAngle, BALL_SETTLED_Y);
      ballAngleRef.current = anchoredAngle;
      return;
    }

    if (spinState.completed) {
      wheel.rotation.y += delta * IDLE_WHEEL_SPEED;
      ball.visible = true;

      const anchoredAngle = spinState.finalAngle + wheel.rotation.y;
      setBallPolarPosition(ball, BALL_SETTLED_RADIUS, anchoredAngle, BALL_SETTLED_Y);
      ballAngleRef.current = anchoredAngle;
      return;
    }

    if (spinState.phase === "WAIT_PICKUP") {
      wheel.rotation.y += delta * PRE_LAUNCH_WHEEL_SPEED;
      ball.visible = true;

      const sourcePocketWorldAngle = spinState.sourcePocketAngle + wheel.rotation.y;
      setBallPolarPosition(
        ball,
        BALL_SETTLED_RADIUS,
        sourcePocketWorldAngle,
        BALL_SETTLED_Y
      );
      ballAngleRef.current = sourcePocketWorldAngle;

      if (sourcePocketWorldAngle >= spinState.pickupPocketAngle) {
        spinState.phase = "WAIT_LAUNCH";
        ball.visible = false;
      }

      return;
    }

    if (spinState.phase === "WAIT_LAUNCH") {
      wheel.rotation.y += delta * PRE_LAUNCH_WHEEL_SPEED;
      ball.visible = false;

      const sourcePocketWorldAngle = spinState.sourcePocketAngle + wheel.rotation.y;

      if (sourcePocketWorldAngle >= spinState.launchPocketAngle) {
        spinState.phase = "WALL_ORBIT";
        spinState.launchedAt = performance.now();
        emitSpinSfxOnce(spinState, "BALL_LAUNCH", onRouletteSfx);
        ball.visible = true;
        setBallPolarPosition(
          ball,
          BALL_WALL_ORBIT_RADIUS,
          CAMERA_CLOCK_5PM_ANGLE,
          BALL_WALL_ORBIT_Y
        );
        ballAngleRef.current = CAMERA_CLOCK_5PM_ANGLE;
      }

      return;
    }

    const launchedElapsed = performance.now() - spinState.launchedAt;
    const totalSpinMs =
      BALL_RAPID_ORBIT_MS +
      BALL_SLOW_ORBIT_MS +
      BALL_SPIRAL_MS +
      BALL_CLICK_SETTLE_MS;
    const spinProgress = clamp01(launchedElapsed / totalSpinMs);
    const wheelSpeed = getWheelSpeedAtSpinProgress(spinProgress);

    wheel.rotation.y += delta * wheelSpeed;
    ball.visible = true;

    const rapidEnd = BALL_RAPID_ORBIT_MS;
    const slowEnd = rapidEnd + BALL_SLOW_ORBIT_MS;
    const spiralEnd = slowEnd + BALL_SPIRAL_MS;
    const lateDecelDurationMs =
      BALL_SLOW_ORBIT_MS + BALL_SPIRAL_MS + BALL_CLICK_SETTLE_MS;

    let ballAngle = CAMERA_CLOCK_5PM_ANGLE;
    let ballRadius = BALL_WALL_ORBIT_RADIUS;
    let ballY = BALL_WALL_ORBIT_Y;

    if (launchedElapsed < rapidEnd) {
      const progress = clamp01(launchedElapsed / BALL_RAPID_ORBIT_MS);
      const wallChatter = Math.sin(progress * TWO_PI * 18) * 0.018;

      ballAngle = CAMERA_CLOCK_5PM_ANGLE - TWO_PI * 6 * progress;
      ballRadius = BALL_WALL_ORBIT_RADIUS + wallChatter;
      ballY = BALL_WALL_ORBIT_Y + Math.abs(wallChatter) * 0.55;
    } else {
      if (spinState.decelStartAngle === null || spinState.decelEndAngle === null) {
        emitSpinSfxOnce(spinState, "LATE_DECEL_START", onRouletteSfx);

        const decelStartProgress = clamp01(rapidEnd / totalSpinMs);
        const remainingWheelRotation = estimateWheelRotationDeltaForSpinProgress(
          decelStartProgress,
          1,
          totalSpinMs
        );
        const projectedFinalWheelRotation = wheel.rotation.y + remainingWheelRotation;
        const projectedSettledTargetAngle =
          spinState.finalAngle + projectedFinalWheelRotation;
        const minimumLateClockwiseTravel = TWO_PI * 2.08;

        spinState.decelStartAngle = CAMERA_CLOCK_5PM_ANGLE - TWO_PI * 6;
        spinState.decelEndAngle = getClockwiseTargetAngleAfterMinimumTravel(
          spinState.decelStartAngle,
          projectedSettledTargetAngle,
          minimumLateClockwiseTravel
        );
      }

      const lateProgress = clamp01((launchedElapsed - rapidEnd) / lateDecelDurationMs);
      const finalApproachProgress = applyFinalApproachFriction(lateProgress);
      const lateEasedProgress = easeOutCubic(finalApproachProgress);
      const currentLateAngle = THREE.MathUtils.lerp(
        spinState.decelStartAngle,
        spinState.decelEndAngle,
        lateEasedProgress
      );
      const remainingLateAngle = Math.abs(spinState.decelEndAngle - currentLateAngle);

      if (
        !spinState.finalApproachSlowdownStarted &&
        remainingLateAngle <= FINAL_APPROACH_FIRST_SLOWDOWN_POCKETS * POCKET_ARC
      ) {
        spinState.finalApproachSlowdownStarted = true;
      }

      if (
        !spinState.finalApproachSecondSlowdownStarted &&
        remainingLateAngle <= FINAL_APPROACH_SECOND_SLOWDOWN_POCKETS * POCKET_ARC
      ) {
        spinState.finalApproachSecondSlowdownStarted = true;
      }

      if (
        !spinState.finalApproachTipSlowdownStarted &&
        remainingLateAngle <= FINAL_APPROACH_TIP_SLOWDOWN_POCKETS * POCKET_ARC
      ) {
        spinState.finalApproachTipSlowdownStarted = true;
      }

      ballAngle = currentLateAngle;

      if (launchedElapsed < slowEnd) {
        const progress = clamp01((launchedElapsed - rapidEnd) / BALL_SLOW_ORBIT_MS);
        const wallChatter = Math.sin(progress * TWO_PI * 7) * (1 - progress) * 0.016;

        ballRadius = BALL_WALL_ORBIT_RADIUS + wallChatter;
        ballY = THREE.MathUtils.lerp(
          BALL_WALL_ORBIT_Y,
          BALL_WALL_ORBIT_Y - 0.045,
          progress
        );
      } else if (launchedElapsed < spiralEnd) {
        emitSpinSfxOnce(spinState, "SPIRAL_TO_NUMBER_BAND", onRouletteSfx);

        const progress = clamp01((launchedElapsed - slowEnd) / BALL_SPIRAL_MS);
        const easedProgress = easeInOutSine(progress);

        ballRadius = THREE.MathUtils.lerp(
          BALL_WALL_ORBIT_RADIUS,
          BALL_NUMBER_BAND_RADIUS,
          easedProgress
        );
        ballY = THREE.MathUtils.lerp(
          BALL_WALL_ORBIT_Y - 0.045,
          BALL_NUMBER_BAND_Y,
          easedProgress
        );
      } else {
        const progress = clamp01((launchedElapsed - spiralEnd) / BALL_CLICK_SETTLE_MS);

        emitSpinSfxOnce(spinState, "CLICK_1", onRouletteSfx);

        if (progress >= 1 / 3) {
          emitSpinSfxOnce(spinState, "CLICK_2", onRouletteSfx);
        }

        if (progress >= 2 / 3) {
          emitSpinSfxOnce(spinState, "FINAL_CLUNK", onRouletteSfx);
        }

        if (spinState.clickStartAngle === null) {
          spinState.clickStartAngle = ballAngle;
        }

        ballRadius = THREE.MathUtils.lerp(
          BALL_NUMBER_BAND_RADIUS,
          BALL_SETTLED_RADIUS,
          easeOutCubic(progress)
        );
        ballY = THREE.MathUtils.lerp(
          BALL_NUMBER_BAND_Y,
          BALL_SETTLED_Y,
          easeOutCubic(progress)
        );

        const clickIndex = Math.min(2, Math.floor(progress * 3));
        const clickStart = clickIndex / 3;
        const clickProgress = clamp01((progress - clickStart) * 3);
        const finalApproachWeight = spinState.finalApproachTipSlowdownStarted
          ? 0.84
          : spinState.finalApproachSecondSlowdownStarted
            ? 0.9
            : spinState.finalApproachSlowdownStarted
              ? 0.95
              : 1;
        const clickHop =
          Math.sin(clickProgress * Math.PI) *
          (clickIndex === 2 ? 0.072 : 0.043) *
          (1 - progress * 0.26) *
          finalApproachWeight;

        ballY += clickHop;
        ballRadius +=
          Math.sin(clickProgress * Math.PI) *
          (clickIndex === 2 ? 0.03 : 0.018) *
          finalApproachWeight;

        const nearMissRockback = getNearMissRockback(
          progress,
          spinState.nearMissRockbackEnabled
        );
        const winningPocketWobble = getWinningPocketWobble(
          progress,
          spinState.winningPocketWobbleEnabled
        );

        ballAngle += nearMissRockback.angleOffset + winningPocketWobble.angleOffset;
        ballRadius += nearMissRockback.radiusOffset + winningPocketWobble.radiusOffset;
        ballY += nearMissRockback.yOffset + winningPocketWobble.yOffset;

        if (progress >= 1 && !spinState.completed) {
          spinState.completed = true;
          ball.visible = true;
          settledAngleRef.current = spinState.finalAngle;

          const lockedFinalWorldAngle =
            spinState.decelEndAngle ?? spinState.finalAngle + wheel.rotation.y;

          wheel.rotation.y = lockedFinalWorldAngle - spinState.finalAngle;
          ballAngleRef.current = lockedFinalWorldAngle;
          setBallPolarPosition(
            ball,
            BALL_SETTLED_RADIUS,
            lockedFinalWorldAngle,
            BALL_SETTLED_Y
          );
          setVisualResult(spinState.result);

          window.setTimeout(() => {
            spinStateRef.current = null;
            onSpinCompleteRef.current?.(spinState.result);
          }, 180);

          return;
        }
      }
    }

    setBallPolarPosition(ball, ballRadius, ballAngle, ballY);
    ballAngleRef.current = ballAngle;
  });

  return (
    <>
      <ambientLight intensity={0.44} />
      <directionalLight position={[3.8, 6.2, 4.4]} intensity={1.55} castShadow />
      <pointLight position={[-3.4, 2.2, -2.6]} intensity={0.52} color="#5d120f" />
      <pointLight position={[2.4, 2.8, 2.7]} intensity={1.75} color="#efc96f" />
      <pointLight position={[0, 2.6, 1.6]} intensity={0.16} color="#7b1818" />

      <group position={[0, BASIN_SEATED_Y, BASIN_SEATED_Z]} scale={WHEEL_SCENE_SCALE}>
        <WoodenBasin />
      </group>

      <group ref={wheelRef} rotation={[0, 0, 0]} scale={WHEEL_SCENE_SCALE} position={[0, WHEEL_SEATED_Y, WHEEL_SEATED_Z]}>
        <mesh receiveShadow position={[0, -0.08, 0]}>
          <cylinderGeometry args={[2.68, 3.08, 0.44, 128]} />
          <meshStandardMaterial color="#1a0805" roughness={0.5} metalness={0.22} emissive="#120302" emissiveIntensity={0.08} />
        </mesh>

        <mesh receiveShadow position={[0, 0.07, 0]}>
          <cylinderGeometry args={[2.38, 2.78, 0.22, 128]} />
          <meshStandardMaterial color="#111111" roughness={0.44} metalness={0.36} />
        </mesh>

        <WheelCraterSurface />

        <mesh receiveShadow position={[0, 0.118, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[2.5, 0.04, 12, 128]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.16}
          />
        </mesh>


        <mesh receiveShadow position={[0, 0.221, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <ringGeometry args={[2.044, 2.088, 128]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.16}
            side={THREE.DoubleSide}
            polygonOffset
            polygonOffsetFactor={-2.15}
            polygonOffsetUnits={-2.15}
          />
        </mesh>

        <mesh receiveShadow={false} position={[0, 0.219, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <ringGeometry args={[2.048, 2.084, 128]} />
          <meshBasicMaterial
            color="#fff0ad"
            transparent
            opacity={0.13}
            depthWrite={false}
            side={THREE.DoubleSide}
            polygonOffset
            polygonOffsetFactor={-2.35}
            polygonOffsetUnits={-2.35}
          />
        </mesh>

        <mesh receiveShadow position={[0, 0.207, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[2.064, 0.016, 10, 128]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.16}
          />
        </mesh>

        <GoldPocketFloorBand />

        <InnerBurgundyHill />

        <mesh receiveShadow position={[0, 0.81, 0]}>
          <cylinderGeometry args={[0.34, 0.5, 0.28, 96]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.16}
          />
        </mesh>

        <mesh receiveShadow position={[0, 1.035, 0]}>
          <sphereGeometry args={[0.17, 32, 16]} />
          <meshStandardMaterial
            color="#efc96f"
            roughness={0.18}
            metalness={0.94}
            emissive="#6f450f"
            emissiveIntensity={0.16}
          />
        </mesh>

        <GoldCentreSpinnerCross />

        <RoulettePrintedLabelBand />
        <SlopedOuterGoldTrim />

        {AMERICAN_ROULETTE_WHEEL.map((pocket, index) => (
          <PocketPlate key={`pocket-${pocket}`} index={index} />
        ))}

      </group>

      <mesh ref={ballRef} castShadow>
        <sphereGeometry args={[BALL_RADIUS, 32, 24]} />
        <meshStandardMaterial color="#f2f2f2" roughness={0.24} metalness={0.05} />
      </mesh>

      <RouletteStatusTextureLabel
        text={visualResult ? `RESULT ${visualResult}` : status || "DRAFT"}
      />
    </>
  );
}

function RouletteWheel3D({
  status,
  result,
  spinNonce,
  onSpinComplete,
  onRouletteSfx,
  rouletteSfxMuted = false,
  onRouletteSfxMuteToggle,
}) {
  return (
    <RouletteWheelErrorBoundary status={status}>
      <div className="roulette-wheel-3d-shell" style={{ position: "relative" }}>
        <button
          type="button"
          className="pogforts-arcade-sound-toggle"
          aria-label={rouletteSfxMuted ? "Unmute roulette sounds" : "Mute roulette sounds"}
          title={rouletteSfxMuted ? "Sound off — click to unmute" : "Sound on — click to mute"}
          onClick={onRouletteSfxMuteToggle}
        >
          <span aria-hidden="true">{rouletteSfxMuted ? "🔇" : "🔊"}</span>
        </button>

        <Canvas
          shadows
          dpr={[1, 1.75]}
          camera={{ position: [0, 3.88, 5.5], fov: 39 }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
            premultipliedAlpha: false,
          }}
        >
          <CameraRig />
          <fog attach="fog" args={["#070206", 11, 18]} />
          <Suspense fallback={null}>
            <RouletteWheelScene
              status={status}
              result={result}
              spinNonce={spinNonce}
              onSpinComplete={onSpinComplete}
              onRouletteSfx={onRouletteSfx}
            />
          </Suspense>
        </Canvas>
      </div>
    </RouletteWheelErrorBoundary>
  );
}

export default RouletteWheel3D;
